// src/pages/AdminCommunications.js
//
// Admin Communications page.
// Sub-tabs: Compose | History
//
// Compose:
//   - Tournament selector (from props)
//   - Scope: Tournament participants or Pool participants
//   - Participant checklist with Select All / Deselect All
//   - Subject input
//   - Rich text body (custom RichTextEditor)
//   - CC admin toggle
//   - Preview button (modal with branded HTML render)
//   - Send button
//
// History:
//   - Table of sent communications, newest first
//   - Expandable rows to re-read body

import React, { useEffect, useState, useCallback, useRef } from 'react';
import DOMPurify  from 'dompurify';

import {
    getPools,
    getCommunicationsRecipients,
    sendCommunication,
    getCommunications,
    getCurrentUser,
} from '../services/api';

const SUB_TABS = [
    { id: 'compose', label: 'Compose' },
    { id: 'history', label: 'History' },
];

// ---------------------------------------------------------------------------
// RichTextEditor — custom contentEditable rich text editor
// No external library; React 18 compatible.
// ---------------------------------------------------------------------------
function RichTextEditor({ value, onChange }) {
    const editorRef = useRef(null);
    const isInternalUpdate = useRef(false);

    // Sync external value changes into the editor only on mount.
    useEffect(() => {
        if (editorRef.current && !editorRef.current.innerHTML && value) {
            editorRef.current.innerHTML = value;
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const handleInput = () => {
        isInternalUpdate.current = true;
        onChange(editorRef.current.innerHTML);
    };

    const exec = (command, value = null) => {
        editorRef.current.focus();
        document.execCommand(command, false, value);
        handleInput();
    };

    const handleLink = () => {
        const url = window.prompt('Enter URL:', 'https://');
        if (url) exec('createLink', url);
    };

    const ToolbarBtn = ({ cmd, label, value }) => (
        <button
            type="button"
            onMouseDown={e => { e.preventDefault(); value !== undefined ? exec(cmd, value) : exec(cmd); }}
            className="px-2 py-1 text-gray-400 hover:text-white hover:bg-gray-700 rounded text-sm font-bold transition-colors"
            title={cmd}>
            {label}
        </button>
    );

    return (
        <div className="rounded-lg overflow-hidden border border-gray-700 focus-within:border-orange-500 transition-colors">
            {/* Toolbar */}
            <div className="flex items-center gap-0.5 px-2 py-1.5 bg-gray-800 border-b border-gray-700 flex-wrap">
                <ToolbarBtn cmd="bold"          label="B" />
                <ToolbarBtn cmd="italic"        label="I" />
                <ToolbarBtn cmd="underline"     label="U" />
                <span className="text-gray-700 mx-1">|</span>
                <ToolbarBtn cmd="insertOrderedList"   label="1." />
                <ToolbarBtn cmd="insertUnorderedList" label="•" />
                <span className="text-gray-700 mx-1">|</span>
                <button
                    type="button"
                    onMouseDown={e => { e.preventDefault(); handleLink(); }}
                    className="px-2 py-1 text-gray-400 hover:text-white hover:bg-gray-700 rounded text-sm transition-colors"
                    title="Insert link">
                    🔗
                </button>
                <ToolbarBtn cmd="removeFormat"  label="✕" />
            </div>
            {/* Editable area */}
            <div
                ref={editorRef}
                contentEditable
                suppressContentEditableWarning
                onInput={handleInput}
                data-placeholder="Write your message here…"
                className="min-h-40 px-4 py-3 text-gray-200 text-sm leading-relaxed focus:outline-none bg-gray-900 empty:before:content-[attr(data-placeholder)] empty:before:text-gray-600"
                style={{ minHeight: '160px' }}
            />
        </div>
    );
}

// ---------------------------------------------------------------------------
// Preview modal
// ---------------------------------------------------------------------------
function PreviewModal({ subject, body, signerName, orgBrandName, onClose }) {
    const clean = DOMPurify.sanitize(body);

    // Minimal branded wrapper for preview (mirrors email.js htmlWrapper).
    const previewHtml = `
        <div style="background:#0a0a0f;min-height:100%;padding:32px 16px;font-family:Arial,Helvetica,sans-serif;">
          <div style="max-width:560px;margin:0 auto;background:#111118;border-radius:12px;overflow:hidden;border:1px solid #2a2a3a;">
            <div style="background:#111118;border-bottom:3px solid #f97316;padding:24px 32px;">
              <span style="font-size:22px;font-weight:900;color:#f97316;letter-spacing:-0.5px;display:block;">${orgBrandName || 'Your Organizer'}</span>
              <span style="font-size:11px;color:#6b7280;letter-spacing:0.5px;">Powered by HebertHoops.com</span>
            </div>
            <div style="padding:32px;">
              <div style="font-size:15px;line-height:1.6;color:#e5e7eb;">${clean}</div>
              <p style="margin:24px 0 0 0;font-size:13px;color:#6b7280;border-top:1px solid #2a2a3a;padding-top:16px;">
                — ${signerName || 'Your Organizer'}<br/>${orgBrandName || 'Your Organizer'}
              </p>
            </div>
          </div>
        </div>`;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4">
            <div className="bg-gray-950 border border-gray-800 rounded-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
                <div className="px-5 py-4 border-b border-gray-800 flex items-center justify-between flex-shrink-0">
                    <div>
                        <p className="text-xs text-gray-500 uppercase tracking-wide mb-0.5">Preview</p>
                        <p className="text-white font-bold text-sm">{subject || '(no subject)'}</p>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-gray-500 hover:text-white text-xl font-bold transition-colors">
                        ✕
                    </button>
                </div>
                <div className="overflow-y-auto flex-1">
                    <iframe
                        srcDoc={previewHtml}
                        title="Email preview"
                        className="w-full border-0"
                        style={{ height: '600px' }}
                        sandbox="allow-same-origin"
                    />
                </div>
            </div>
        </div>
    );
}

// ---------------------------------------------------------------------------
// Compose panel
// ---------------------------------------------------------------------------
function ComposePanel({ activeTournament, showFeedback }) {
    const [pools,          setPools]          = useState([]);
    const [allParticipants, setAllParticipants] = useState([]);
    const [scope,          setScope]          = useState('tournament'); // 'tournament' | pool id
    const [selected,       setSelected]       = useState(new Set());
    const [subject,        setSubject]        = useState('');
    const [body,           setBody]           = useState('');
    const [ccAdmin,        setCcAdmin]        = useState(false);
    const [signerName,     setSignerName]     = useState('');
    const [orgBrandName,   setOrgBrandName]   = useState('');
    const [sending,        setSending]        = useState(false);
    const [showPreview,    setShowPreview]    = useState(false);

    // Load pools and signer info when tournament changes.
    useEffect(() => {
        if (!activeTournament) return;
        getPools(activeTournament.id)
            .then(res => setPools(res.data))
            .catch(() => {});
        getCurrentUser()
            .then(res => {
                setSignerName(res.data.participant.name);
                setOrgBrandName(res.data.participant.organizer_name || res.data.participant.name);
            })
            .catch(() => {});
        setSelected(new Set());
        setScope('tournament');
    }, [activeTournament]);

    // Load the eligible-recipients list whenever the tournament OR the
    // selected scope (all participants vs. one specific pool) changes --
    // this is scoped server-side to who can actually receive a message
    // for this tournament/pool, so nobody ineligible ever appears as
    // selectable here in the first place.
    useEffect(() => {
        if (!activeTournament) return;
        const poolId = scope !== 'tournament' ? parseInt(scope, 10) : undefined;
        getCommunicationsRecipients(activeTournament.id, poolId)
            .then(res => setAllParticipants(res.data))
            .catch(() => setAllParticipants([]));
        setSelected(new Set());
    }, [activeTournament, scope]);

    const visibleParticipants = allParticipants;

    const handleSelectAll  = () => setSelected(new Set(visibleParticipants.map(p => p.id)));
    const handleDeselectAll = () => setSelected(new Set());
    const handleToggle = (id) => {
        setSelected(prev => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    };

    const canSend = selected.size > 0 && subject.trim() && body && body !== '<p><br></p>';

    const handleSend = async () => {
        if (!canSend) return;
        setSending(true);
        try {
            const cleanBody = DOMPurify.sanitize(body);
            const res = await sendCommunication({
                tournament_id:  activeTournament.id,
                pool_id:        scope !== 'tournament' ? parseInt(scope) : undefined,
                recipient_ids:  Array.from(selected),
                subject:        subject.trim(),
                body:           cleanBody,
                cc_admin:       ccAdmin,
            });
            showFeedback(`Email sent to ${res.data.recipient_count} participant${res.data.recipient_count !== 1 ? 's' : ''}`);
            setSubject('');
            setBody('');
            setSelected(new Set());
            setCcAdmin(false);
        } catch (err) {
            showFeedback(err.response?.data?.error || 'Failed to send email', 'error');
        } finally {
            setSending(false);
        }
    };

    if (!activeTournament) {
        return <p className="text-gray-500 text-sm">Select a tournament to compose a message.</p>;
    }

    return (
        <div className="max-w-3xl">
            {showPreview && (
                <PreviewModal
                    subject={subject}
                    body={body}
                    signerName={signerName}
                    orgBrandName={orgBrandName}
                    onClose={() => setShowPreview(false)}
                />
            )}

            {/* Scope selector */}
            <div className="mb-5">
                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">
                    Recipients
                </label>
                <div className="flex gap-2 flex-wrap">
                    <button
                        onClick={() => { setScope('tournament'); setSelected(new Set()); }}
                        className={
                            'px-3 py-1.5 rounded-lg text-xs font-bold transition-colors ' +
                            (scope === 'tournament'
                                ? 'bg-orange-500 text-white'
                                : 'bg-gray-800 text-gray-400 hover:text-white')
                        }>
                        All Tournament Participants
                    </button>
                    {pools.map(p => (
                        <button
                            key={p.id}
                            onClick={() => { setScope(String(p.id)); setSelected(new Set()); }}
                            className={
                                'px-3 py-1.5 rounded-lg text-xs font-bold transition-colors ' +
                                (scope === String(p.id)
                                    ? 'bg-orange-500 text-white'
                                    : 'bg-gray-800 text-gray-400 hover:text-white')
                            }>
                            {p.name}
                        </button>
                    ))}
                </div>
            </div>

            {/* Participant checklist */}
            <div className="bg-gray-900 border border-gray-800 rounded-xl mb-5 overflow-hidden">
                <div className="px-4 py-2.5 border-b border-gray-800 flex items-center justify-between">
                    <p className="text-xs font-bold text-gray-500 uppercase tracking-wide">
                        {selected.size} of {visibleParticipants.length} selected
                    </p>
                    <div className="flex gap-2">
                        <button
                            onClick={handleSelectAll}
                            className="text-xs text-orange-400 hover:text-orange-300 font-bold transition-colors">
                            Select all
                        </button>
                        <span className="text-gray-700">·</span>
                        <button
                            onClick={handleDeselectAll}
                            className="text-xs text-gray-500 hover:text-gray-300 font-bold transition-colors">
                            Deselect all
                        </button>
                    </div>
                </div>
                <div className="divide-y divide-gray-800/50 max-h-48 overflow-y-auto">
                    {visibleParticipants.length === 0 ? (
                        <p className="px-4 py-3 text-gray-500 text-sm">No participants found.</p>
                    ) : visibleParticipants.map(p => (
                        <label key={p.id} className="flex items-center gap-3 px-4 py-2.5 cursor-pointer hover:bg-gray-800/30">
                            <input
                                type="checkbox"
                                checked={selected.has(p.id)}
                                onChange={() => handleToggle(p.id)}
                                className="accent-orange-500 w-4 h-4 flex-shrink-0"
                            />
                            <span className="text-white text-sm">{p.name}</span>
                            <span className="text-gray-600 text-xs">{p.email}</span>
                        </label>
                    ))}
                </div>
            </div>

            {/* Subject */}
            <div className="mb-4">
                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">
                    Subject
                </label>
                <input
                    type="text"
                    value={subject}
                    onChange={e => setSubject(e.target.value)}
                    maxLength={500}
                    placeholder="Email subject"
                    className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-orange-500"
                />
            </div>

            {/* Body — rich text editor */}
            <div className="mb-4">
                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">
                    Message
                </label>
                <RichTextEditor value={body} onChange={setBody} />
            </div>

            {/* CC admin toggle */}
            <div className="flex items-center gap-3 mb-6">
                <input
                    type="checkbox"
                    id="cc-admin"
                    checked={ccAdmin}
                    onChange={e => setCcAdmin(e.target.checked)}
                    className="accent-orange-500 w-4 h-4"
                />
                <label htmlFor="cc-admin" className="text-sm text-gray-400 cursor-pointer">
                    CC organizer email (configured in App Settings → General)
                </label>
            </div>

            {/* Actions */}
            <div className="flex gap-3">
                <button
                    onClick={() => setShowPreview(true)}
                    disabled={!body || body === '<p><br></p>'}
                    className="bg-gray-800 hover:bg-gray-700 disabled:opacity-40 text-white font-bold px-5 py-2.5 rounded-lg text-sm transition-colors">
                    Preview
                </button>
                <button
                    onClick={handleSend}
                    disabled={!canSend || sending}
                    className="bg-orange-500 hover:bg-orange-400 disabled:opacity-40 text-white font-bold px-6 py-2.5 rounded-lg text-sm transition-colors">
                    {sending ? 'Sending…' : `Send to ${selected.size} participant${selected.size !== 1 ? 's' : ''}`}
                </button>
            </div>
        </div>
    );
}

// ---------------------------------------------------------------------------
// History panel
// ---------------------------------------------------------------------------
function HistoryPanel({ activeTournament, showFeedback }) {
    const [comms,   setComms]   = useState([]);
    const [loading, setLoading] = useState(true);
    const [expanded, setExpanded] = useState(new Set());

    const fetchComms = useCallback(() => {
        setLoading(true);
        const params = activeTournament ? { tournament_id: activeTournament.id } : {};
        getCommunications(params)
            .then(res => setComms(res.data))
            .catch(() => showFeedback('Failed to load communications history', 'error'))
            .finally(() => setLoading(false));
    }, [activeTournament, showFeedback]);

    useEffect(() => { fetchComms(); }, [fetchComms]);

    const toggleExpand = (id) => {
        setExpanded(prev => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    };

    if (loading) return <p className="text-gray-500 text-sm">Loading…</p>;

    if (comms.length === 0) {
        return (
            <div className="text-gray-500 text-sm text-center py-12 bg-gray-900 border border-gray-800 rounded-xl">
                No communications sent yet.
            </div>
        );
    }

    return (
        <div className="space-y-3 max-w-3xl">
            {comms.map(c => {
                const open = expanded.has(c.id);
                const recipientNames = (c.recipients || []).map(r => r.name).join(', ');
                return (
                    <div key={c.id} className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
                        <button
                            onClick={() => toggleExpand(c.id)}
                            className="w-full px-4 py-3 flex items-start justify-between hover:bg-gray-800/50 transition-colors text-left">
                            <div className="flex-1 min-w-0 pr-4">
                                <p className="text-white font-bold text-sm truncate">{c.subject}</p>
                                <p className="text-gray-500 text-xs mt-0.5">
                                    {new Date(c.sent_at).toLocaleString('en-US', {
                                        month: 'short', day: 'numeric', year: 'numeric',
                                        hour: 'numeric', minute: '2-digit',
                                    })}
                                    {' · '}
                                    {c.recipients?.length || 0} recipient{c.recipients?.length !== 1 ? 's' : ''}
                                    {c.pool_name && ` · ${c.pool_name}`}
                                    {c.cc_admin && ' · CC\'d organizer'}
                                </p>
                            </div>
                            <span className="text-gray-600 text-xs font-bold flex-shrink-0">{open ? '▲' : '▼'}</span>
                        </button>
                        {open && (
                            <div className="border-t border-gray-800 px-4 py-4">
                                <p className="text-xs text-gray-500 mb-2 font-bold uppercase tracking-wide">Recipients</p>
                                <p className="text-gray-400 text-sm mb-4">{recipientNames || '—'}</p>
                                <p className="text-xs text-gray-500 mb-2 font-bold uppercase tracking-wide">Message</p>
                                <div
                                    className="text-gray-300 text-sm prose prose-invert max-w-none"
                                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(c.body) }}
                                />
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
}

// ---------------------------------------------------------------------------
// AdminCommunications shell
// ---------------------------------------------------------------------------
function AdminCommunications({ activeTournament, showFeedback }) {
    const [subTab, setSubTab] = useState('compose');

    return (
        <div>
            {/* Sub-nav */}
            <div className="flex gap-1 mb-6 border-b border-gray-800">
                {SUB_TABS.map(tab => (
                    <button
                        key={tab.id}
                        onClick={() => setSubTab(tab.id)}
                        className={'px-4 py-2 text-sm font-bold rounded-t-lg transition-colors ' +
                            (subTab === tab.id
                                ? 'bg-gray-950 text-orange-400 border-t border-x border-gray-800'
                                : 'text-gray-500 hover:text-gray-300')}>
                        {tab.label}
                    </button>
                ))}
            </div>

            {subTab === 'compose' && (
                <ComposePanel
                    activeTournament={activeTournament}
                    showFeedback={showFeedback}
                />
            )}
            {subTab === 'history' && (
                <HistoryPanel
                    activeTournament={activeTournament}
                    showFeedback={showFeedback}
                />
            )}
        </div>
    );
}

export default AdminCommunications;